import { useState } from "react";

import {
  CircleCheckIcon,
  InformationIcon,
  LokasiTargetinIcon,
  UploadIcon
} from "@/assets/icons";
import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import { ADD_DATASET_MESSAGE } from "@/constants/message";
import { cn } from "@/lib/utils";
import { AddDatasetSourceOption, EAddDatasetSource } from "@/types/dataset";

const TEST_ID_PREFIX = "add-dataset-dialog";

const DATA_SOURCES: AddDatasetSourceOption[] = [
  {
    key: EAddDatasetSource.UPLOAD,
    title: ADD_DATASET_MESSAGE.UPLOAD_TITLE,
    description: ADD_DATASET_MESSAGE.UPLOAD_DESCRIPTION,
    icon: <UploadIcon className="w-5 h-5 fill-teal-500" />,
    // Opens the existing upload flow on selection, so the submit button stays disabled.
    requiresConfirmation: false
  },
  {
    key: EAddDatasetSource.TARGETIN,
    title: ADD_DATASET_MESSAGE.TARGETIN_TITLE,
    description: ADD_DATASET_MESSAGE.TARGETIN_DESCRIPTION,
    icon: <LokasiTargetinIcon className="w-5 h-5" />,
    requiresConfirmation: true,
    settings: [
      {
        label: ADD_DATASET_MESSAGE.IMPORT_SCOPE_LABEL,
        value: ADD_DATASET_MESSAGE.IMPORT_SCOPE_VALUE
      },
      {
        label: ADD_DATASET_MESSAGE.DUPLICATE_HANDLING_LABEL,
        value: ADD_DATASET_MESSAGE.DUPLICATE_HANDLING_VALUE
      }
    ],
    hint: ADD_DATASET_MESSAGE.DURATION_HINT
  }
];

export interface AddDatasetDialogProps {
  /** Dismisses the picker without choosing a source. */
  onCancel: () => void;
  /** Called with the chosen source when the user confirms. */
  onConfirm: (source: EAddDatasetSource) => void;
}

/** Source picker behind the "Add Dataset" CTA. */
const AddDatasetDialog = ({
  onCancel,
  onConfirm
}: Readonly<AddDatasetDialogProps>) => {
  const [selectedSource, setSelectedSource] =
    useState<EAddDatasetSource | null>(null);

  const selectedOption = DATA_SOURCES.find(({ key }) => key === selectedSource);

  /**
   * Targetin is only marked here and starts on submit. Upload hands over to the
   * existing upload flow as soon as it is picked, since the button is not its way in.
   */
  const handleSelect = (source: AddDatasetSourceOption) => {
    setSelectedSource(source.key);

    if (!source.requiresConfirmation) onConfirm(source.key);
  };

  return (
    <DialogContent
      className="flex flex-col gap-0 max-w-[520px]"
      data-testid={TEST_ID_PREFIX}
    >
      <div className="flex flex-col gap-3">
        <DialogTitle className="text-xl font-semibold leading-7 text-txtHeading">
          {ADD_DATASET_MESSAGE.TITLE}
        </DialogTitle>
        <DialogDescription className="text-sm font-normal text-txtBody">
          {ADD_DATASET_MESSAGE.DESCRIPTION}
        </DialogDescription>

        <div className="flex flex-col gap-3">
          {DATA_SOURCES.map((source) => {
            const isSelected = source.key === selectedSource;

            return (
              <button
                key={source.key}
                type="button"
                aria-pressed={isSelected}
                data-testid={`${TEST_ID_PREFIX}-source-${source.key}`}
                onClick={() => handleSelect(source)}
                className={cn(
                  "flex  gap-3 p-3 text-left border rounded-xl transition-colors items-center",
                  isSelected
                    ? "border-teal-500"
                    : "border-neutral-200 hover:border-teal-200"
                )}
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-teal-50">
                  {source.icon}
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-txtHeading">
                    {source.title}
                  </span>
                  <span className="text-xs text-txtBody">
                    {source.description}
                  </span>
                </span>
                {isSelected && (
                  <CircleCheckIcon className="w-5 h-5 ml-auto shrink-0 fill-teal-500" />
                )}
              </button>
            );
          })}

          {!!selectedOption?.settings?.length && (
            <div
              className="border rounded-xl border-neutral-200 divide-y divide-neutral-200"
              data-testid={`${TEST_ID_PREFIX}-settings`}
            >
              {selectedOption.settings.map(({ label, value }) => (
                <div
                  key={label}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <span className="text-xs text-txtBody">{label}</span>
                  <span className="text-xs font-semibold text-txtHeading">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {selectedOption?.hint && (
            <div className="flex items-center gap-2 p-3 border rounded-xl border-royal-blue/30 bg-royal-blue/5">
              <InformationIcon className="w-4 h-4 shrink-0 fill-royal-blue" />
              <p className="text-xs text-txtHeading">{selectedOption.hint}</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-row w-full gap-3 mt-6">
        <Button
          variant="outline"
          className="py-2.5 h-auto w-full rounded-xl font-semibold text-sm"
          data-testid={`${TEST_ID_PREFIX}-cancel`}
          onClick={onCancel}
        >
          {ADD_DATASET_MESSAGE.CANCEL}
        </Button>
        <Button
          variant="default"
          className="py-2.5 h-auto w-full rounded-xl font-semibold text-sm"
          data-testid={`${TEST_ID_PREFIX}-confirm`}
          // Only sources that submit through this button can enable it.
          disabled={!selectedOption?.requiresConfirmation}
          onClick={() => selectedSource && onConfirm(selectedSource)}
        >
          {ADD_DATASET_MESSAGE.SUBMIT}
        </Button>
      </div>
    </DialogContent>
  );
};

export default AddDatasetDialog;
